/* Cart drawer.

   Opened from the header cart icon, never from an add to cart — that path is
   the centred confirmation in minery-cart.js, and the two are deliberately
   separate surfaces.

   The element is <minery-cart-drawer>, not <cart-drawer>: Dawn's product-form.js
   does `document.querySelector('cart-notification') || document.querySelector('cart-drawer')`,
   so taking that name would put this panel into the add-to-cart path as soon as
   the confirmation was ever removed. Dawn's own cart-drawer.js is not loaded.

   The panel holds no cart state of its own. It renders
   sections/minery-cart-drawer-body.liquid, refetches it on every open, and
   takes the same section back from the `sections` parameter of each
   /cart/change.js response, so quantities and money strings are always
   Liquid-rendered and never recomputed here. */

(function () {
  'use strict';

  if (customElements.get('minery-cart-drawer')) return;

  var BODY_SECTION = 'minery-cart-drawer-body';

  // Focus handed back after a tap keeps the screen reader in place, but
  // Safari on iPhone drew a ring round the button for it. .m-nofocusring
  // (minery.css) hides that ring until the button next loses focus.
  // The last thing the shopper used: a pointer (mouse, finger, pen) or the
  // keyboard. A dialog opened by a pointer takes focus itself rather than
  // letting showModal() ring its close button as though already chosen.
  var byKeyboard = false;
  document.addEventListener('keydown', function (e) {
    if (!e.metaKey && !e.ctrlKey && !e.altKey) byKeyboard = true;
  }, true);
  document.addEventListener('pointerdown', function () {
    byKeyboard = false;
  }, true);

  function quietOpenFocus(dialog) {
    if (byKeyboard) return;
    dialog.setAttribute('tabindex', '-1');
    dialog.focus({ preventScroll: true });
  }

  function quietFocus(el) {
    if (window.matchMedia && window.matchMedia('(hover: none), (pointer: coarse)').matches) {
      el.classList.add('m-nofocusring');
      el.addEventListener('blur', function drop() {
        el.classList.remove('m-nofocusring');
        el.removeEventListener('blur', drop);
      });
    }
    el.focus({ preventScroll: true });
  }

  function nodeFrom(html, selector) {
    if (!html) return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  /* ------------------------------------------------------------------
     Gesture maths
     ------------------------------------------------------------------ */

  /* Where a flick would coast to if nothing stopped it. This is the
     exponential-decay form UIScrollView uses, not the v^2/2a from a physics
     textbook — the difference is what makes a thrown panel land where the
     hand expected rather than short of it. */
  var DECELERATION = 0.998;

  function project(velocity) {
    return (velocity / 1000) * DECELERATION / (1 - DECELERATION);
  }

  /* Resistance that grows the further the panel is pushed past its resting
     edge. A hard stop reads as the interface having frozen; resistance reads
     as "still listening, but there is nothing more this way". */
  function rubberband(overshoot, dimension) {
    var c = 0.55;
    return (overshoot * dimension * c) / (dimension + c * Math.abs(overshoot));
  }

  function prefersReducedMotion() {
    return (
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  class MineryCartDrawer extends HTMLElement {
    constructor() {
      super();

      this.dialog = this.querySelector('[data-m-cdraw-dialog]');
      this.body = this.querySelector('[data-m-cdraw-body]');
      this.opener = null;
      this.closeTimer = null;
      this.raf = null;
      this.suppressClick = false;

      // Delegated: the whole body is replaced on every cart change, so
      // per-element listeners would be re-bound or leak on each update.
      this.addEventListener('click', function (e) {
        if (e.target.closest('[data-m-cdraw-close]')) {
          e.preventDefault();
          this.close();
          return;
        }

        var step = e.target.closest('[data-m-cdraw-step]');
        if (step) {
          e.preventDefault();
          this.step(step);
          return;
        }

        var remove = e.target.closest('[data-m-cdraw-remove]');
        if (remove) {
          e.preventDefault();
          this.setQuantity(remove, 0);
        }
      }.bind(this));

      this.addEventListener('change', function (e) {
        if (e.target.matches('[data-m-cdraw-qty]')) {
          var next = parseInt(e.target.value, 10);
          // Anything unparseable means the field was cleared or scribbled in;
          // put the line back rather than deleting it on a typo.
          if (isNaN(next) || next < 0) {
            this.refresh();
            return;
          }
          this.setQuantity(e.target, next);
        }
      }.bind(this));

      if (this.dialog) {
        // With showModal() a click on the backdrop reports the dialog itself as
        // the target, since the backdrop is its pseudo-element.
        this.dialog.addEventListener('click', function (e) {
          if (e.target === this.dialog) this.close();
        }.bind(this));

        // Escape is handled by the UA and fires `cancel` before `close`. Taking
        // it over keeps the slide-out instead of the panel vanishing.
        this.dialog.addEventListener('cancel', function (e) {
          e.preventDefault();
          this.close();
        }.bind(this));

        this.dialog.addEventListener('close', function () {
          this.dialog.classList.remove('is-open');
          if (this.opener && document.contains(this.opener)) quietFocus(this.opener);
        }.bind(this));

        this.initDrag();
      }
    }

    /* ------------------------------------------------------------------
       Open / close
       ------------------------------------------------------------------ */

    open(opener) {
      if (!this.dialog) return;

      this.opener = opener || null;

      if (!this.dialog.open) {
        // The shop page's quick view and the add-to-cart confirmation are both
        // <dialog>s. Opening on top of one would stack two modals in the top
        // layer, so close whatever is already open first.
        var self = this;
        Array.prototype.forEach.call(document.querySelectorAll('dialog[open]'), function (d) {
          if (d !== self.dialog) d.close();
        });

        // A previous drag may have left an inline position on the panel, which
        // would otherwise outrank the stylesheet and open it part-way out.
        this.cancelSpring();
        this.resetDrag();

        this.dialog.showModal();
        quietOpenFocus(this.dialog);

        // The panel starts translated off-screen. Releasing it in a later frame
        // is what gives the transition a start value to animate from — set in
        // the same frame as showModal() the browser collapses both into one
        // style resolution and the panel simply appears.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            self.dialog.classList.add('is-open');
          });
        });
      }

      // Always refetch: the confirmation modal can have changed the cart since
      // this body was last rendered. A body that is already populated stays on
      // screen while the request is in flight, so reopening never flashes.
      this.refresh();
    }

    close() {
      if (!this.dialog || !this.dialog.open) return;

      // Hand the panel back to the stylesheet before asking it to slide out;
      // an inline position left by a drag would sit on top of the transition.
      this.cancelSpring();
      this.resetDrag();
      this.dialog.classList.remove('is-dragging');
      this.dialog.classList.remove('is-open');

      // Wait for the slide-out, but never trust the transition alone —
      // transitionend does not fire if the transition was never applied.
      var done = function () {
        clearTimeout(this.closeTimer);
        this.dialog.removeEventListener('transitionend', onEnd);
        if (this.dialog.open) this.dialog.close();
      }.bind(this);

      var onEnd = function (e) {
        if (e.target === this.dialog && e.propertyName === 'translate') done();
      }.bind(this);

      // With prefers-reduced-motion the transition is removed altogether, so
      // there is nothing to wait for. Falling through to the 450ms timer held
      // the scroll lock and the top layer on a page whose drawer had already
      // vanished — a third of a second of a store that ignores you.
      if (prefersReducedMotion()) {
        done();
        return;
      }

      this.dialog.addEventListener('transitionend', onEnd);
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(done, 450);
    }

    /* ------------------------------------------------------------------
       Drag to dismiss

       The panel tracks the pointer 1:1 from wherever it was grabbed, resists
       past its own edge, and on release is thrown to wherever the gesture was
       actually heading rather than snapping from where the finger happened to
       stop. Everything the UA gives a <dialog> — Escape, the close button,
       the backdrop — keeps working unchanged.
       ------------------------------------------------------------------ */

    initDrag() {
      var dialog = this.dialog;
      var self = this;

      var pointer = null;   // the one pointer being followed
      var decided = false;  // has this gesture committed to being horizontal?
      var startX = 0;
      var startY = 0;
      var x = 0;
      var width = 0;
      var history = [];

      /* A completed drag ends in a click on the dialog, which is precisely
         what the backdrop handler reads as "close me". Captured here so it is
         swallowed before either that or the delegated handler sees it. */
      dialog.addEventListener('click', function (e) {
        if (!self.suppressClick) return;
        self.suppressClick = false;
        e.stopPropagation();
        e.preventDefault();
      }, true);

      dialog.addEventListener('pointerdown', function (e) {
        // One finger. Switching to a second mid-drag would otherwise jump the
        // panel to wherever the new one landed.
        if (pointer !== null) return;
        if (!e.isPrimary || e.button !== 0) return;

        // Controls keep their own presses.
        if (e.target.closest('button, a, input, select, textarea, label')) return;

        // Outside the panel's own box is the backdrop, which closes on click
        // and is not a thing to drag.
        var rect = dialog.getBoundingClientRect();
        if (e.clientX < rect.left || e.clientX > rect.right) return;

        self.cancelSpring();
        pointer = e.pointerId;
        decided = false;
        width = rect.width;
        x = 0;
        startX = e.clientX;
        startY = e.clientY;
        history = [{ x: e.clientX, t: e.timeStamp }];
      });

      dialog.addEventListener('pointermove', function (e) {
        if (pointer === null || e.pointerId !== pointer) return;

        var dx = e.clientX - startX;
        var dy = e.clientY - startY;

        if (!decided) {
          // ~10px of hysteresis before committing, so a tap with a tremor in
          // it is still a tap. Whichever axis wins takes the gesture: down the
          // list is a scroll and is left to the browser, across is a dismiss.
          if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
          if (Math.abs(dy) >= Math.abs(dx)) {
            pointer = null;
            return;
          }
          decided = true;
          dialog.classList.add('is-dragging');
          // Keeps the gesture alive even when the pointer leaves the panel.
          try { dialog.setPointerCapture(pointer); } catch (err) {}
        }

        x = dx < 0 ? rubberband(dx, width) : dx;
        self.place(x, width);

        // A short trailing history, not just the last event: one frame of
        // jitter should not decide what the whole gesture meant.
        history.push({ x: e.clientX, t: e.timeStamp });
        if (history.length > 6) history.shift();
      });

      function release(e) {
        if (pointer === null || e.pointerId !== pointer) return;

        var committed = decided;
        pointer = null;
        decided = false;
        if (!committed) return;

        self.suppressClick = true;

        var first = history[0];
        var last = history[history.length - 1];
        var span = last.t - first.t;

        // Too short a span is noise rather than a flick; let distance alone
        // decide instead of dividing by almost nothing.
        var velocity = span >= 5 ? ((last.x - first.x) / span) * 1000 : 0;
        velocity = Math.max(-3000, Math.min(3000, velocity));

        // Where it is going, not where it was let go: a short fast flick
        // should throw the panel out, a long slow drag should not.
        var dismiss = x + project(velocity) > width * 0.5;

        if (prefersReducedMotion()) {
          self.settleDrag(dismiss);
          return;
        }

        self.spring(x, dismiss ? width : 0, velocity, width, function () {
          self.settleDrag(dismiss);
        });
      }

      dialog.addEventListener('pointerup', release);
      dialog.addEventListener('pointercancel', release);
    }

    place(x, width) {
      this.dialog.style.translate = (x / width) * 100 + '% 0';
      // 1 at rest, 0 fully dragged out, so the scrim tracks the gesture.
      var p = 1 - x / width;
      this.dialog.style.setProperty('--m-cdraw-p', String(p < 0 ? 0 : p > 1 ? 1 : p));
    }

    cancelSpring() {
      if (this.raf) {
        cancelAnimationFrame(this.raf);
        this.raf = null;
      }
    }

    /* Apple's two parameters rather than the mass/stiffness/damping triplet.

       Critically damped, because a drawer settling back into place should not
       bounce: overshoot would say "springy", and what is wanted here is "it
       went exactly where you put it". The release velocity is carried in as
       the spring's own starting velocity, which is what removes the seam
       between the finger letting go and the animation taking over.

       The response was 0.34 first, and measured out at 517-567ms to throw the
       panel off screen — against 220ms for dismissing the same panel with its
       close button. One outcome at two very different speeds is the thing to
       avoid, so it came down to 0.24, which measures 383-417ms to dismiss and
       367-417ms to settle back. Still longer than the button, which is right:
       this one is carrying momentum the button never has. */
    spring(from, to, initialVelocity, width, done) {
      var self = this;
      this.cancelSpring();

      var x = from;
      var v = initialVelocity;
      var dampingRatio = 1.0;
      var response = 0.24;
      var omega = (2 * Math.PI) / response;
      var last = null;

      function frame(now) {
        if (last === null) last = now;
        // Clamped so a backgrounded tab returning does not integrate one
        // enormous step and fling the panel across the screen.
        var dt = Math.min((now - last) / 1000, 1 / 30);
        last = now;

        var a = -omega * omega * (x - to) - 2 * dampingRatio * omega * v;
        v += a * dt;
        x += v * dt;

        // Off-screen is finished; there is nothing left to watch settle.
        var arrived =
          (to > 0 && x >= to) ||
          (Math.abs(x - to) < 0.4 && Math.abs(v) < 12);

        if (arrived) {
          self.raf = null;
          done();
          return;
        }

        self.place(x, width);
        self.raf = requestAnimationFrame(frame);
      }

      this.raf = requestAnimationFrame(frame);
    }

    settleDrag(dismiss) {
      this.dialog.classList.remove('is-dragging');

      if (dismiss) {
        // Drop .is-open first so the stylesheet's resting position — fully
        // translated out — is what the inline value is handed back to, and
        // clearing the inline style then changes nothing on screen.
        this.dialog.classList.remove('is-open');
        this.resetDrag();
        if (this.dialog.open) this.dialog.close();
        return;
      }

      this.resetDrag();
    }

    resetDrag() {
      this.dialog.style.removeProperty('translate');
      this.dialog.style.removeProperty('--m-cdraw-p');
    }

    /* ------------------------------------------------------------------
       Rendering

       One entry point, so the open path and the change path cannot drift.
       ------------------------------------------------------------------ */

    render(html) {
      var fresh = nodeFrom(html, '[data-m-cdraw-inner]');
      if (fresh && this.body) this.body.innerHTML = fresh.outerHTML;
    }

    refresh() {
      var base = this.getAttribute('data-cart-url') || '/cart';
      var joiner = base.indexOf('?') === -1 ? '?' : '&';

      return fetch(base + joiner + 'section_id=' + BODY_SECTION)
        .then(function (r) {
          if (!r.ok) throw new Error(String(r.status));
          return r.text();
        })
        .then(this.render.bind(this))
        .catch(function () {
          /* leave whatever is on screen — a failed refresh must not blank the
             panel the shopper is looking at */
        });
    }

    /* ------------------------------------------------------------------
       Quantity
       ------------------------------------------------------------------ */

    step(button) {
      var line = button.closest('[data-m-cdraw-line]');
      if (!line) return;

      var input = line.querySelector('[data-m-cdraw-qty]');
      if (!input) return;

      var current = parseInt(input.value, 10);
      if (isNaN(current)) current = 0;

      var next = current + parseInt(button.getAttribute('data-m-cdraw-step'), 10);
      if (next < 0) next = 0;

      this.setQuantity(button, next);
    }

    setQuantity(origin, quantity) {
      var line = origin.closest('[data-m-cdraw-line]');
      if (!line || line.classList.contains('is-busy')) return;

      var key = line.getAttribute('data-m-cdraw-line');
      if (!key) return;

      // Locks this row for the round trip, so a double-tap on + cannot send a
      // second update computed from a quantity the server has already changed.
      line.classList.add('is-busy');

      var url = (window.routes && window.routes.cart_change_url) || '/cart/change';

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: [BODY_SECTION, 'cart-icon-bubble'].join(','),
          sections_url: window.location.pathname
        })
      })
        .then(function (r) {
          if (!r.ok) throw new Error(String(r.status));
          return r.json();
        })
        .then(function (state) {
          var sections = state.sections || {};
          this.render(sections[BODY_SECTION]);
          this.updateBubble(sections['cart-icon-bubble']);
        }.bind(this))
        .catch(function () {
          // Put the row back the way the server has it rather than leaving a
          // number on screen that was never accepted.
          line.classList.remove('is-busy');
          this.refresh();
        }.bind(this));
    }

    /* The header icon is outside this element, so it is updated here rather
       than by the section swap. Dawn's own section, so it arrives wrapped in
       .shopify-section — same unwrapping as minery-cart.js. */
    updateBubble(html) {
      var source = nodeFrom(html, '.shopify-section');
      var target = document.getElementById('cart-icon-bubble');
      if (source && target) target.innerHTML = source.innerHTML;
    }
  }

  customElements.define('minery-cart-drawer', MineryCartDrawer);

  /* The cart icon lives in the header markup, not inside this element, so the
     opener is bound on the document. Delegated because sections/cart-icon-bubble
     replaces the anchor's contents after every cart change.

     The anchor keeps its href, so with this script blocked the icon still
     navigates to the cart page. */
  document.addEventListener('click', function (e) {
    var opener = e.target.closest('[data-m-cdraw-open]');
    if (!opener) return;

    // Let modified clicks through so "open in new tab" still works.
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

    var drawer = document.querySelector('minery-cart-drawer');
    if (!drawer || typeof drawer.open !== 'function') return;

    e.preventDefault();
    drawer.open(opener);
  });
})();
